import React, { useState, useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SidebarNavigation from '../components/SidebarNavigation';
import { getAuthHeaders } from '../lib/auth';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Upload, Crown, Target, Image as ImageIcon, Type, Square, Circle, Download, 
  Trash2, Copy, Layers, Plus, ChevronLeft, AlignLeft, AlignCenter, AlignRight, 
  X, Undo, Redo, Search, ArrowUp, ArrowDown, RotateCcw, Home, Briefcase
} from 'lucide-react';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANVAS_SIZES = {
  instagram_square: { width: 1080, height: 1080, label: 'Instagram Square (1080x1080)' },
  instagram_story: { width: 1080, height: 1920, label: 'Instagram Story (1080x1920)' },
  facebook_post: { width: 1200, height: 630, label: 'Facebook Post (1200x630)' },
  poster: { width: 1080, height: 1350, label: 'Poster (1080x1350)' }
};

const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Raleway',
  'Poppins', 'Merriweather', 'Playfair Display', 'Source Sans Pro', 'Ubuntu',
  'Nunito', 'PT Sans', 'Rubik', 'Work Sans', 'Bebas Neue', 'Dancing Script',
  'Pacifico', 'Lobster', 'Great Vibes', 'Indie Flower'
];

const BUSINESS_INFO_TEMPLATES = [
  {
    id: 'modern_blue',
    name: 'Modern Blue Wave',
    thumbnail: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/w3skydy5_images%20%283%29.jpeg',
    frameImage: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/w3skydy5_images%20%283%29.jpeg',
    style: { bgColor: '#ffffff', textColor: '#1e3a8a' }
  },
  {
    id: 'decorative_frame',
    name: 'Decorative Frame',
    thumbnail: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/dycbqajn_pngtree-flyer-frame-png-image_3941399.png',
    frameImage: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/dycbqajn_pngtree-flyer-frame-png-image_3941399.png',
    style: { bgColor: 'transparent', textColor: '#000000' }
  },
  {
    id: 'colorful_banner',
    name: 'Colorful Banner',
    thumbnail: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/ngjihqjm_pngtree-colorful-text-shape-box-tag-banner-template-png-image_6402568.jpg',
    frameImage: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/ngjihqjm_pngtree-colorful-text-shape-box-tag-banner-template-png-image_6402568.jpg',
    style: { bgColor: 'transparent', textColor: '#ffffff' }
  },
  {
    id: 'tricolor_business',
    name: 'Tricolor Business',
    thumbnail: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/u4o1ntay_png-clipart-web-banner-flyer-tricolor-business-banner-green-orange-and-blue-frame-collage-template-angle-thumbnail.png',
    frameImage: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/u4o1ntay_png-clipart-web-banner-flyer-tricolor-business-banner-green-orange-and-blue-frame-collage-template-angle-thumbnail.png',
    style: { bgColor: 'transparent', textColor: '#000000' }
  },
  {
    id: 'elegant_curves',
    name: 'Elegant Curves',
    thumbnail: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/9rzh35fa_czNmcy1wcml2YXRlL3Jhd3BpeGVsX2ltYWdlcy93ZWJzaXRlX2NvbnRlbnQvbHIvdjEwMjgtNTEucG5n.jpeg',
    frameImage: 'https://customer-assets.emergentagent.com/job_design-suite-pro/artifacts/9rzh35fa_czNmcy1wcml2YXRlL3Jhd3BpeGVsX2ltYWdlcy93ZWJzaXRlX2NvbnRlbnQvbHIvdjEwMjgtNTEucG5n.jpeg',
    style: { bgColor: 'transparent', textColor: '#000000' }
  },
  {
    id: 'ornate_border',
    name: 'Ornate Border',
    thumbnail: 'https://images.unsplash.com/photo-1731167711054-0ac4d0b41b1a?crop=entropy&cs=srgb&fm=jpg&q=85',
    frameImage: 'https://images.unsplash.com/photo-1731167711054-0ac4d0b41b1a?crop=entropy&cs=srgb&fm=jpg&q=85',
    style: { bgColor: 'transparent', textColor: '#000000' }
  },
  {
    id: 'geometric_frame',
    name: 'Geometric Frame',
    thumbnail: 'https://images.unsplash.com/photo-1731167711131-0c18b5fb6b18?crop=entropy&cs=srgb&fm=jpg&q=85',
    frameImage: 'https://images.unsplash.com/photo-1731167711131-0c18b5fb6b18?crop=entropy&cs=srgb&fm=jpg&q=85',
    style: { bgColor: 'transparent', textColor: '#000000' }
  },
  {
    id: 'blue_curves',
    name: 'Blue Curves',
    thumbnail: 'https://images.unsplash.com/photo-1646310997905-14eb66d1e04a?crop=entropy&cs=srgb&fm=jpg&q=85',
    frameImage: 'https://images.unsplash.com/photo-1646310997905-14eb66d1e04a?crop=entropy&cs=srgb&fm=jpg&q=85',
    style: { bgColor: 'transparent', textColor: '#ffffff' }
  },
  {
    id: 'abstract_wave',
    name: 'Abstract Wave',
    thumbnail: 'https://images.unsplash.com/photo-1646310997921-9565eff3eac0?crop=entropy&cs=srgb&fm=jpg&q=85',
    frameImage: 'https://images.unsplash.com/photo-1646310997921-9565eff3eac0?crop=entropy&cs=srgb&fm=jpg&q=85',
    style: { bgColor: 'transparent', textColor: '#ffffff' }
  },
  {
    id: 'professional_banner',
    name: 'Professional Banner',
    thumbnail: 'https://images.pexels.com/photos/7233099/pexels-photo-7233099.jpeg',
    frameImage: 'https://images.pexels.com/photos/7233099/pexels-photo-7233099.jpeg',
    style: { bgColor: 'transparent', textColor: '#000000' }
  }
];

const TEMPLATE_CATEGORIES = [
  {
    id: 'birthday',
    name: 'Birthday',
    searchQuery: 'birthday celebration',
    template: {
      elements: [
        { type: 'text', x: 100, y: 150, w: 880, h: 120, zIndex: 2, props: { text: 'Happy Birthday!', fontSize: 72, fontFamily: 'Pacifico', color: '#ff1493', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 300, w: 880, h: 80, zIndex: 2, props: { text: 'Wishing you a wonderful day!', fontSize: 32, fontFamily: 'Dancing Script', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#ff69b4', backgroundOpacity: 0.8 } }
      ]
    }
  },
  {
    id: 'anniversary',
    name: 'Anniversary',
    searchQuery: 'wedding anniversary',
    template: {
      elements: [
        { type: 'text', x: 100, y: 200, w: 880, h: 100, zIndex: 2, props: { text: 'Happy Anniversary', fontSize: 64, fontFamily: 'Great Vibes', color: '#8b4513', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 330, w: 880, h: 70, zIndex: 2, props: { text: 'Celebrating Your Love', fontSize: 36, fontFamily: 'Playfair Display', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#d4af37', backgroundOpacity: 0.9 } }
      ]
    }
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    searchQuery: 'thanksgiving dinner',
    template: {
      elements: [
        { type: 'text', x: 100, y: 180, w: 880, h: 110, zIndex: 2, props: { text: 'Happy Thanksgiving', fontSize: 68, fontFamily: 'Lobster', color: '#ff8c00', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 320, w: 880, h: 75, zIndex: 2, props: { text: 'Grateful for You', fontSize: 38, fontFamily: 'Merriweather', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#8b4513', backgroundOpacity: 0.85 } }
      ]
    }
  },
  {
    id: 'business',
    name: 'Business',
    searchQuery: 'business professional',
    template: {
      elements: [
        { type: 'text', x: 100, y: 250, w: 880, h: 90, zIndex: 2, props: { text: 'Your Business Name', fontSize: 58, fontFamily: 'Montserrat', color: '#1e3a8a', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 370, w: 880, h: 65, zIndex: 2, props: { text: 'Professional Services', fontSize: 32, fontFamily: 'Inter', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#3b82f6', backgroundOpacity: 0.9 } }
      ]
    }
  },
  {
    id: 'social',
    name: 'Social Media',
    searchQuery: 'social media colorful',
    template: {
      elements: [
        { type: 'text', x: 100, y: 220, w: 880, h: 100, zIndex: 2, props: { text: 'Follow Us!', fontSize: 70, fontFamily: 'Bebas Neue', color: '#ffffff', fontWeight: 'bold', textAlign: 'center', backgroundColor: '#8b5cf6', backgroundOpacity: 0.85 } },
        { type: 'text', x: 100, y: 350, w: 880, h: 70, zIndex: 2, props: { text: '@YourHandle', fontSize: 42, fontFamily: 'Roboto', color: '#fbbf24', fontWeight: 'bold', textAlign: 'center' } }
      ]
    }
  }
];

// Load Google Fonts dynamically
const loadGoogleFont = (fontName) => {
  const formattedName = fontName.replace(/ /g, '+');
  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${formattedName}:wght@400;600;700&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

export default function DigiEditor() {
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState('instagram_square');
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showStockImages, setShowStockImages] = useState(false);
  const [stockImages, setStockImages] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [showMyDesigns, setShowMyDesigns] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [removingBackground, setRemovingBackground] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showImageOptions, setShowImageOptions] = useState(null);
  const [showBackgroundOptions, setShowBackgroundOptions] = useState(false);
  const [businessInfoForm, setBusinessInfoForm] = useState({
    businessName: '',
    ownerName: '',
    location: '',
    email: '',
    phone: '',
    logoUrl: ''
  });
  const [showBusinessInfoModal, setShowBusinessInfoModal] = useState(false);
  const [selectedBusinessTemplate, setSelectedBusinessTemplate] = useState(null);
  const [showBusinessTemplates, setShowBusinessTemplates] = useState(false);
  const [propertiesPanelOpen, setPropertiesPanelOpen] = useState(true);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);

  useEffect(() => {
    // Load popular Google Fonts on mount
    ['Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Poppins'].forEach(font => loadGoogleFont(font));
  }, []);

  const startNewDesign = () => {
    setShowWelcome(false);
    setElements([]);
    setHistory([]);
    setHistoryIndex(-1);
    setBackgroundColor('#ffffff');
    setBackgroundImage('');
    setCurrentDesignId(null);
  };

  const saveDesign = async () => {
    try {
      const designData = {
        elements,
        canvasSize,
        backgroundColor,
        backgroundImage
      };
      
      if (currentDesignId) {
        await axios.put(
          `${API}/strategy/designs/${currentDesignId}`,
          designData,
          { headers: getAuthHeaders() }
        );
        toast.success('Design saved!');
      } else {
        const response = await axios.post(
          `${API}/strategy/designs`,
          designData,
          { headers: getAuthHeaders() }
        );
        setCurrentDesignId(response.data.designId);
        toast.success('Design saved!');
      }
    } catch (error) {
      console.error('Failed to save design:', error);
      toast.error('Failed to save design');
    }
  };

  const loadMyDesigns = async () => {
    try {
      const response = await axios.get(`${API}/strategy/designs`, {
        headers: getAuthHeaders()
      });
      setMyDesigns(response.data.designs || []);
      setShowMyDesigns(true);
    } catch (error) {
      console.error('Failed to load designs:', error);
      toast.error('Failed to load designs');
    }
  };

  const loadDesign = (design) => {
    setElements(design.elements || []);
    setCanvasSize(design.canvasSize || 'instagram_square');
    setBackgroundColor(design.backgroundColor || '#ffffff');
    setBackgroundImage(design.backgroundImage || '');
    setCurrentDesignId(design.id);
    setShowMyDesigns(false);
    setShowWelcome(false);
    toast.success('Design loaded!');
  };

  const deleteDesign = async (designId) => {
    try {
      await axios.delete(`${API}/strategy/designs/${designId}`, {
        headers: getAuthHeaders()
      });
      setMyDesigns(myDesigns.filter(d => d.id !== designId));
      toast.success('Design deleted!');
    } catch (error) {
      console.error('Failed to delete design:', error);
      toast.error('Failed to delete design');
    }
  };

  const addBusinessInfoToCanvas = () => {
    if (!businessInfoForm.businessName && !businessInfoForm.ownerName) {
      toast.error('Please enter at least business name or owner name');
      return;
    }
    
    const size = CANVAS_SIZES[canvasSize];
    const contactY = size.height - 300;
    const newElements = [...elements];
    
    // Add selected decorative frame as background image
    if (selectedBusinessTemplate) {
      const template = BUSINESS_INFO_TEMPLATES.find(t => t.id === selectedBusinessTemplate);
      if (template) {
        newElements.push({
          id: Date.now(),
          type: 'image',
          x: 0,
          y: contactY,
          w: size.width,
          h: 300,
          zIndex: elements.length + 1,
          props: { 
            src: template.frameImage,
            opacity: 1
          }
        });
      }
    } else {
      // Default: Bottom decorative background
      newElements.push({
        id: Date.now(),
        type: 'rectangle',
        x: 0,
        y: contactY + 80,
        w: size.width,
        h: size.height - (contactY + 80),
        zIndex: elements.length + 1,
        props: { color: '#5c3d2e', opacity: 1, borderRadius: 0 }
      });
      
      // Top decorative background
      newElements.push({
        id: Date.now() + 1,
        type: 'rectangle',
        x: 0,
        y: contactY + 50,
        w: size.width,
        h: 60,
        zIndex: elements.length + 2,
        props: { color: '#ff6b35', opacity: 0.9, borderRadius: 0 }
      });
    }
    
    // Logo
    if (businessInfoForm.logoUrl) {
      newElements.push({
        id: Date.now() + 2,
        type: 'image',
        x: 50,
        y: contactY + 60,
        w: 80,
        h: 80,
        zIndex: elements.length + 3,
        props: { 
          src: businessInfoForm.logoUrl, 
          opacity: 1,
          borderRadius: 40,
          logoShape: 'circle'
        }
      });
    }
    
    // Business Name
    if (businessInfoForm.businessName) {
      newElements.push({
        id: Date.now() + 3,
        type: 'text',
        x: businessInfoForm.logoUrl ? 150 : 50,
        y: contactY + 60,
        w: size.width - (businessInfoForm.logoUrl ? 200 : 100),
        h: 35,
        zIndex: elements.length + 4,
        props: {
          text: businessInfoForm.businessName,
          fontSize: 28,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'bold',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    // Owner Name
    if (businessInfoForm.ownerName) {
      newElements.push({
        id: Date.now() + 4,
        type: 'text',
        x: businessInfoForm.logoUrl ? 150 : 50,
        y: contactY + 100,
        w: size.width - (businessInfoForm.logoUrl ? 200 : 100),
        h: 25,
        zIndex: elements.length + 5,
        props: {
          text: businessInfoForm.ownerName,
          fontSize: 18,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    // Location
    if (businessInfoForm.location) {
      newElements.push({
        id: Date.now() + 5,
        type: 'text',
        x: businessInfoForm.logoUrl ? 150 : 50,
        y: contactY + 130,
        w: size.width - (businessInfoForm.logoUrl ? 200 : 100),
        h: 25,
        zIndex: elements.length + 6,
        props: {
          text: `📍 ${businessInfoForm.location}`,
          fontSize: 16,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    // Email
    if (businessInfoForm.email) {
      newElements.push({
        id: Date.now() + 6,
        type: 'text',
        x: businessInfoForm.logoUrl ? 150 : 50,
        y: contactY + 155,
        w: size.width - (businessInfoForm.logoUrl ? 200 : 100),
        h: 25,
        zIndex: elements.length + 7,
        props: {
          text: `✉️ ${businessInfoForm.email}`,
          fontSize: 16,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    // Phone
    if (businessInfoForm.phone) {
      newElements.push({
        id: Date.now() + 7,
        type: 'text',
        x: businessInfoForm.logoUrl ? 150 : 50,
        y: contactY + 180,
        w: size.width - (businessInfoForm.logoUrl ? 200 : 100),
        h: 25,
        zIndex: elements.length + 8,
        props: {
          text: `📞 ${businessInfoForm.phone}`,
          fontSize: 16,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    setElements(newElements);
    saveToHistory(newElements);
    setShowBusinessInfoModal(false);
    toast.success('Business info added to canvas!');
  };

  const loadTemplate = async (template) => {
    setShowTemplates(false);
    setShowWelcome(false);
    toast.info('Loading template...');
    
    try {
      const pexelsResponse = await axios.get(
        `https://api.pexels.com/v1/search?query=${template.searchQuery}&per_page=1`,
        {
          headers: {
            Authorization: 'wqs31dEukjAHuUM7ARtAcIsEGVWUus6cQ4ZaDbTOkXCsGFJNuPFg2RLJ'
          }
        }
      );
      
      if (pexelsResponse.data.photos && pexelsResponse.data.photos.length > 0) {
        setBackgroundImage(pexelsResponse.data.photos[0].src.large);
      }
      
      setElements(JSON.parse(JSON.stringify(template.template.elements)));
      saveToHistory(template.template.elements);
      toast.success('Template loaded!');
    } catch (error) {
      console.error('Failed to load template:', error);
      setElements(JSON.parse(JSON.stringify(template.template.elements)));
      saveToHistory(template.template.elements);
      toast.warning('Template loaded without background image');
    }
  };

  const addTextElement = () => {
    const size = CANVAS_SIZES[canvasSize];
    const newElement = {
      id: Date.now(),
      type: 'text',
      x: 100,
      y: 100,
      w: 300,
      h: 60,
      zIndex: elements.length + 1,
      props: {
        text: 'Double click to edit',
        fontSize: 24,
        fontFamily: 'Inter',
        color: '#000000',
        fontWeight: 'normal',
        textAlign: 'left',
        backgroundColor: 'transparent',
        backgroundOpacity: 0.8
      }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setSelectedElement(newElement.id);
    toast.success('Text element added');
  };

  const addShape = (shape) => {
    const newElement = {
      id: Date.now(),
      type: shape,
      x: 100,
      y: 100,
      w: 200,
      h: 200,
      zIndex: elements.length + 1,
      props: {
        color: '#3b82f6',
        opacity: 1,
        borderRadius: shape === 'rectangle' ? 8 : 0
      }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setSelectedElement(newElement.id);
    toast.success(`${shape} added`);
  };

  const saveToHistory = (newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newElements)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      toast.success('Undo');
    } else {
      toast.error('Nothing to undo');
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      toast.success('Redo');
    } else {
      toast.error('Nothing to redo');
    }
  };

  const updateElement = (id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
    saveToHistory(newElements);
  };

  const updateElementProps = (id, propUpdates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, props: { ...el.props, ...propUpdates } } : el
    );
    setElements(newElements);
    saveToHistory(newElements);
  };

  const deleteElement = (id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    saveToHistory(newElements);
    if (selectedElement === id) setSelectedElement(null);
    toast.success('Element deleted');
  };

  const duplicateElement = (id) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...element,
        id: Date.now(),
        x: element.x + 20,
        y: element.y + 20
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Element duplicated');
    }
  };

  const moveLayerUp = (id) => {
    const index = elements.findIndex(el => el.id === id);
    if (index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      newElements.forEach((el, i) => el.zIndex = i);
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Layer moved up');
    }
  };

  const moveLayerDown = (id) => {
    const index = elements.findIndex(el => el.id === id);
    if (index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      newElements.forEach((el, i) => el.zIndex = i);
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Layer moved down');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const removeBackground = window.confirm('Remove background from this image?');
      
      if (removeBackground) {
        setRemovingBackground(true);
        toast.info('Removing background...');
        
        try {
          const formData = new FormData();
          formData.append('image_file', file);
          formData.append('size', 'auto');
          
          const response = await axios.post(
            'https://api.remove.bg/v1.0/removebg',
            formData,
            {
              headers: {
                'X-Api-Key': 'demo-key',
              },
              responseType: 'blob'
            }
          );
          
          const reader = new FileReader();
          reader.onloadend = () => {
            addImageToCanvas(reader.result);
            setRemovingBackground(false);
          };
          reader.readAsDataURL(response.data);
        } catch (error) {
          console.error('Background removal failed:', error);
          toast.error('Background removal failed. Using original image.');
          setRemovingBackground(false);
          
          const reader = new FileReader();
          reader.onloadend = () => addImageToCanvas(reader.result);
          reader.readAsDataURL(file);
        }
      } else {
        const reader = new FileReader();
        reader.onloadend = () => addImageToCanvas(reader.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const addImageToCanvas = (imageSrc) => {
    const newElement = {
      id: Date.now(),
      type: 'image',
      x: 100,
      y: 100,
      w: 300,
      h: 300,
      zIndex: elements.length + 1,
      props: { src: imageSrc, opacity: 1 }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    toast.success('Image uploaded');
  };

  const searchStockImages = async (query = null) => {
    setLoadingStock(true);
    const searchQuery = query || 'business';
    
    try {
      const pexelsResponse = await axios.get(
        `https://api.pexels.com/v1/search?query=${searchQuery}&per_page=30`,
        {
          headers: {
            Authorization: 'wqs31dEukjAHuUM7ARtAcIsEGVWUus6cQ4ZaDbTOkXCsGFJNuPFg2RLJ'
          }
        }
      );
      
      if (pexelsResponse.data.photos && pexelsResponse.data.photos.length > 0) {
        setStockImages(pexelsResponse.data.photos.map(p => ({
          id: p.id,
          url: p.src.large,
          thumbnail: p.src.medium,
          photographer: p.photographer
        })));
        setShowStockImages(true);
        toast.success(`Found ${pexelsResponse.data.photos.length} images`);
      } else {
        toast.error('No images found. Try a different search term.');
      }
    } catch (error) {
      console.error('Failed to load stock images:', error);
      toast.error('Failed to load stock images. Please try again.');
    } finally {
      setLoadingStock(false);
    }
  };

  const addStockImageToCanvas = (imageUrl) => {
    // If replacing an existing image
    if (showImageOptions) {
      updateElementProps(showImageOptions, { src: imageUrl });
      setShowImageOptions(null);
      setShowStockImages(false);
      toast.success('Image replaced!');
      return;
    }
    
    // If setting as background (check if opened from background options)
    if (showBackgroundOptions === 'fromStock') {
      setBackgroundImage(imageUrl);
      setShowStockImages(false);
      setShowBackgroundOptions(false);
      toast.success('Background updated!');
      return;
    }
    
    // Adding new image
    const newElement = {
      id: Date.now(),
      type: 'image',
      x: 100,
      y: 100,
      w: 400,
      h: 400,
      zIndex: elements.length + 1,
      props: { src: imageUrl, opacity: 1 }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setShowStockImages(false);
    toast.success('Image added to canvas');
  };
  
  const replaceImageFromUpload = (elementId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const removeBackground = window.confirm('Remove background from this image?');
        
        if (removeBackground) {
          setRemovingBackground(true);
          toast.info('Removing background...');
          
          try {
            const formData = new FormData();
            formData.append('image_file', file);
            formData.append('size', 'auto');
            
            const response = await axios.post(
              'https://api.remove.bg/v1.0/removebg',
              formData,
              {
                headers: { 'X-Api-Key': 'demo-key' },
                responseType: 'blob'
              }
            );
            
            const reader = new FileReader();
            reader.onloadend = () => {
              updateElementProps(elementId, { src: reader.result });
              setRemovingBackground(false);
              toast.success('Image replaced!');
            };
            reader.readAsDataURL(response.data);
          } catch (error) {
            console.error('Background removal failed:', error);
            toast.error('Background removal failed. Using original image.');
            setRemovingBackground(false);
            
            const reader = new FileReader();
            reader.onloadend = () => {
              updateElementProps(elementId, { src: reader.result });
              toast.success('Image replaced!');
            };
            reader.readAsDataURL(file);
          }
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            updateElementProps(elementId, { src: reader.result });
            toast.success('Image replaced!');
          };
          reader.readAsDataURL(file);
        }
      }
    };
    input.click();
    setShowImageOptions(null);
  };
  
  const replaceBackgroundFromStock = () => {
    setShowBackgroundOptions('fromStock');
    searchStockImages('abstract background');
  };
  
  const replaceBackgroundFromUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setBackgroundImage(reader.result);
          toast.success('Background updated!');
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
    setShowBackgroundOptions(false);
  };
  
  const setBackgroundAsColor = () => {
    setBackgroundImage('');
    toast.info('Select a color from the background section');
    setShowBackgroundOptions(false);
  };

  const exportAsImage = async () => {
    if (canvasRef.current) {
      try {
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: backgroundColor,
          scale: 2
        });
        
        const link = document.createElement('a');
        link.download = `design-${Date.now()}.png`;
        link.href = canvas.toDataURL();
        link.click();
        
        toast.success('Poster exported successfully!');
      } catch (error) {
        console.error('Export failed:', error);
        toast.error('Failed to export poster');
      }
    }
  };

  const selectedEl = elements.find(el => el.id === selectedElement);
  const size = CANVAS_SIZES[canvasSize];

  // Welcome Screen
  if (showWelcome) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <SidebarNavigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)] p-6">
          <Card className="max-w-4xl w-full shadow-2xl">
            <CardHeader className="text-center pb-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Digi Editor
              </CardTitle>
              <p className="text-gray-600 mt-2">Create stunning social media posters and graphics</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Button
                  onClick={startNewDesign}
                  className="h-32 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                >
                  <Plus className="w-8 h-8" />
                  <span className="font-semibold">Start Blank</span>
                </Button>
                
                <Button
                  onClick={() => setShowTemplates(true)}
                  variant="outline"
                  className="h-32 flex flex-col items-center justify-center gap-3 border-2 hover:border-purple-500 hover:bg-purple-50"
                >
                  <Target className="w-8 h-8 text-purple-600" />
                  <span className="font-semibold">Use Template</span>
                </Button>
                
                <Button
                  onClick={loadMyDesigns}
                  variant="outline"
                  className="h-32 flex flex-col items-center justify-center gap-3 border-2 hover:border-purple-500 hover:bg-purple-50"
                >
                  <Layers className="w-8 h-8 text-purple-600" />
                  <span className="font-semibold">My Designs</span>
                </Button>
              </div>
              
              <div className="pt-6 border-t">
                <h3 className="font-semibold text-gray-800 mb-3">Features:</h3>
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Drag & drop editing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Stock images from Pexels</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Google Fonts integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Auto-save designs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Background removal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                    <span>Export as PNG</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Templates Modal */}
        {showTemplates && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Pre-made Templates</h3>
                  <p className="text-sm text-gray-600">Choose a template to customize</p>
                </div>
                <Button onClick={() => setShowTemplates(false)} variant="ghost" size="sm">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                <div className="grid grid-cols-3 gap-4">
                  {TEMPLATE_CATEGORIES.map(template => (
                    <div 
                      key={template.id}
                      onClick={() => loadTemplate(template)}
                      className="border-2 rounded-lg overflow-hidden hover:border-purple-500 transition-all cursor-pointer group"
                    >
                      <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-6">
                        <div className="text-center">
                          <Target className="w-16 h-16 mx-auto mb-3 text-purple-600" />
                          <p className="text-lg font-bold text-purple-900">{template.name}</p>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 group-hover:bg-purple-50 transition-colors">
                        <p className="text-sm font-medium text-gray-700 text-center">Click to use</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* My Designs Modal */}
        {showMyDesigns && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">My Designs</h3>
                  <p className="text-sm text-gray-600">Load and edit your saved designs</p>
                </div>
                <Button onClick={() => setShowMyDesigns(false)} variant="ghost" size="sm">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                {myDesigns.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Layers className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium text-gray-600 mb-2">No saved designs yet</p>
                    <p className="text-sm">Create a poster and it will be auto-saved here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {myDesigns.map(design => (
                      <div key={design.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="aspect-square bg-gray-100 flex items-center justify-center p-4">
                          <div className="text-center">
                            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p className="text-xs text-gray-600">{design.canvasSize}</p>
                          </div>
                        </div>
                        <div className="p-3 space-y-2">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {new Date(design.createdAt).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => loadDesign(design)}
                              size="sm"
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                            >
                              Load
                            </Button>
                            <Button
                              onClick={() => deleteDesign(design.id)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Editor View
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SidebarNavigation />
      
      {/* Top Toolbar */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Button onClick={() => setShowWelcome(true)} variant="ghost" size="sm" className="hover:bg-gray-100">
            <Home className="w-4 h-4 mr-1" />
            Home
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-lg font-semibold text-gray-800">Digi Editor</h1>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <Button 
              onClick={undo} 
              disabled={historyIndex <= 0}
              variant="ghost" 
              size="sm"
              className="hover:bg-white disabled:opacity-40"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="w-4 h-4" />
            </Button>
            <Button 
              onClick={redo} 
              disabled={historyIndex >= history.length - 1}
              variant="ghost" 
              size="sm"
              className="hover:bg-white disabled:opacity-40"
              title="Redo (Ctrl+Y)"
            >
              <Redo className="w-4 h-4" />
            </Button>
          </div>
          <Button onClick={saveDesign} variant="outline">
            Save
          </Button>
          <Button onClick={exportAsImage} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Hamburger Menu Button - Left side - Show when panel is closed */}
        {!leftPanelOpen && (
          <button
            onClick={() => setLeftPanelOpen(true)}
            className="fixed left-4 top-20 z-50 bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-3 shadow-lg transition-colors"
            title="Open Tools & Settings Panel"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Left Sidebar */}
        {leftPanelOpen && (
        <>
        <div className="w-20 border-r border-gray-200 bg-white flex flex-col items-center py-4 gap-2">
          <button
            onClick={addTextElement}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="Add Text"
          >
            <Type className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Text</span>
          </button>
          
          <button
            onClick={() => addShape('rectangle')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="Add Rectangle"
          >
            <Square className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Shape</span>
          </button>
          
          <button
            onClick={() => addShape('circle')}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="Add Circle"
          >
            <Circle className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Circle</span>
          </button>
          
          <label className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer w-16">
            <Upload className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Upload</span>
            <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </label>
          
          <button
            onClick={() => searchStockImages()}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="Search Stock Images"
          >
            <Search className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Images</span>
          </button>
          
          <div className="my-4 h-px w-12 bg-gray-200" />
          
          <button
            onClick={loadMyDesigns}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="My Designs"
          >
            <Layers className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Designs</span>
          </button>
          
          <button
            onClick={() => setShowTemplates(true)}
            className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
            title="Templates"
          >
            <Target className="w-6 h-6 text-gray-700" />
            <span className="text-xs text-gray-600">Template</span>
          </button>
        </div>

        {/* Expanded Panel */}
        <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Header with Close Button */}
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-800">Tools & Settings</h3>
              <button
                onClick={() => setLeftPanelOpen(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded p-1 transition-colors"
                title="Close Panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Canvas Settings */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Canvas Size</h3>
              <Select value={canvasSize} onValueChange={setCanvasSize}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CANVAS_SIZES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Background Controls */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Background</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Color</label>
                  <Input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="h-10 w-full cursor-pointer"
                  />
                </div>
                <Button
                  onClick={() => setShowBackgroundOptions(true)}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <ImageIcon className="w-3 h-3 mr-2" />
                  Change Background
                </Button>
                {backgroundImage && (
                  <Button
                    onClick={() => {
                      setBackgroundImage('');
                      toast.success('Background removed');
                    }}
                    variant="outline"
                    size="sm"
                    className="w-full text-red-600 hover:bg-red-50"
                  >
                    <X className="w-3 h-3 mr-2" />
                    Remove Image
                  </Button>
                )}
              </div>
            </div>
            
            {/* Business Info Button */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Quick Add</h3>
              <Button
                onClick={() => setShowBusinessTemplates(true)}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Briefcase className="w-3 h-3 mr-2" />
                Add Business Info
              </Button>
            </div>

            {/* Layers List */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Layers ({elements.length})
              </h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {elements.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">No elements yet</p>
                ) : (
                  [...elements].reverse().map((el) => (
                    <div
                      key={el.id}
                      onClick={() => setSelectedElement(el.id)}
                      className={`p-2 rounded-lg text-xs cursor-pointer flex items-center justify-between transition-colors ${
                        selectedElement === el.id 
                          ? 'bg-purple-100 border-2 border-purple-400' 
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-1 truncate">
                        {el.type === 'text' && <Type className="w-3 h-3 text-gray-600" />}
                        {el.type === 'image' && <ImageIcon className="w-3 h-3 text-gray-600" />}
                        {el.type === 'rectangle' && <Square className="w-3 h-3 text-gray-600" />}
                        {el.type === 'circle' && <Circle className="w-3 h-3 text-gray-600" />}
                        <span className="truncate text-gray-700 font-medium">
                          {el.type === 'text' ? el.props.text.substring(0, 15) + '...' : el.type.charAt(0).toUpperCase() + el.type.slice(1)}
                        </span>
                      </div>
                      <div className="flex gap-0.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLayerUp(el.id);
                          }}
                          className="p-1 hover:bg-purple-100 rounded transition-colors"
                          title="Move Up"
                        >
                          <ArrowUp className="w-3 h-3 text-gray-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveLayerDown(el.id);
                          }}
                          className="p-1 hover:bg-purple-100 rounded transition-colors"
                          title="Move Down"
                        >
                          <ArrowDown className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
        </>
        )}

        {/* Canvas Area */}
        <div className="flex-1 bg-gray-100 flex items-center justify-center p-8 overflow-auto">
          <div
            ref={canvasRef}
            style={{
              width: size.width * 0.7,
              height: size.height * 0.7,
              backgroundColor: backgroundColor,
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
              backgroundSize: 'cover',
              position: 'relative',
              overflow: 'hidden'
            }}
            className="shadow-2xl rounded-sm"
          >
            {elements.map((element) => (
              <Rnd
                key={element.id}
                size={{ width: element.w / 2, height: element.h / 2 }}
                position={{ x: element.x / 2, y: element.y / 2 }}
                onDragStop={(e, d) => updateElement(element.id, { x: d.x * 2, y: d.y * 2 })}
                onResizeStop={(e, direction, ref, delta, position) => {
                  updateElement(element.id, {
                    w: parseInt(ref.style.width) * 2,
                    h: parseInt(ref.style.height) * 2,
                    x: position.x * 2,
                    y: position.y * 2
                  });
                }}
                bounds="parent"
                style={{ 
                  zIndex: selectedElement === element.id ? 9999 : element.zIndex 
                }}
              >
                <div
                  onClick={() => setSelectedElement(element.id)}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: selectedElement === element.id ? '2px solid #8b5cf6' : 'none',
                    cursor: element.type === 'text' ? 'text' : 'move'
                  }}
                >
                  {element.type === 'text' && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedElement(element.id);
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        fontSize: element.props.fontSize / 2,
                        fontFamily: element.props.fontFamily,
                        color: element.props.color,
                        fontWeight: element.props.fontWeight,
                        textAlign: element.props.textAlign,
                        backgroundColor: element.props.backgroundColor || 'transparent',
                        padding: '8px',
                        outline: 'none',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: element.props.textAlign === 'center' ? 'center' : element.props.textAlign === 'right' ? 'flex-end' : 'flex-start',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        position: 'relative',
                        userSelect: 'none'
                      }}
                    >
                      {element.props.text}
                    </div>
                  )}
                  {element.type === 'image' && (
                    <div
                      onClick={() => {
                        setSelectedElement(element.id);
                        setShowImageOptions(element.id);
                      }}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        cursor: 'pointer',
                        borderRadius: element.props.borderRadius ? `${element.props.borderRadius / 2}px` : '0',
                        overflow: 'hidden',
                        position: 'relative'
                      }}
                      title="Click to show options"
                    >
                      <img
                        src={element.props.src}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          opacity: element.props.opacity,
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  )}
                  {element.type === 'rectangle' && (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: element.props.color,
                        opacity: element.props.opacity,
                        borderRadius: element.props.borderRadius
                      }}
                    />
                  )}
                  {element.type === 'circle' && (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: element.props.color,
                        opacity: element.props.opacity,
                        borderRadius: '50%'
                      }}
                    />
                  )}
                </div>
              </Rnd>
            ))}
          </div>
        </div>

        {/* Hamburger Menu Button - Show when panel is closed */}
        {!propertiesPanelOpen && (
          <button
            onClick={() => setPropertiesPanelOpen(true)}
            className="fixed right-4 top-20 z-50 bg-purple-600 hover:bg-purple-700 text-white rounded-lg p-3 shadow-lg transition-colors"
            title="Open Properties Panel"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Right Properties Panel - Collapsible */}
        {propertiesPanelOpen && (
          <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800">
                  {selectedEl ? 'Edit Element' : 'Properties'}
                </h3>
                <button
                  onClick={() => setPropertiesPanelOpen(false)}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded p-1 transition-colors"
                  title="Close Properties Panel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            <div className="space-y-4">
              {selectedEl ? (
                <>
                  {/* Same properties panel content as DigiStrategyWithEditor */}
                  {selectedEl.type === 'text' && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <label className="text-xs font-semibold text-blue-900 block mb-2">✏️ Edit Text Content</label>
                        <textarea
                          value={selectedEl.props.text}
                          onChange={(e) => updateElementProps(selectedEl.id, { text: e.target.value })}
                          className="w-full bg-white border border-gray-300 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="4"
                          placeholder="Type your text here..."
                          style={{
                            fontFamily: selectedEl.props.fontFamily,
                            fontSize: '14px'
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Edit your text here - changes will appear on canvas
                        </p>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Font</label>
                        <Select
                          value={selectedEl.props.fontFamily}
                          onValueChange={(val) => {
                            loadGoogleFont(val);
                            updateElementProps(selectedEl.id, { fontFamily: val });
                          }}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            {GOOGLE_FONTS.map(font => (
                              <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                {font}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Size</label>
                        <Input
                          type="number"
                          value={selectedEl.props.fontSize}
                          onChange={(e) => updateElementProps(selectedEl.id, { fontSize: Number(e.target.value) })}
                          min="12"
                          max="200"
                          className="bg-white"
                        />
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Text Color</label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={selectedEl.props.color}
                            onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                            className="h-10 w-16 cursor-pointer"
                          />
                          <Input
                            type="text"
                            value={selectedEl.props.color}
                            onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                            className="flex-1 bg-white font-mono text-xs"
                          />
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Weight</label>
                        <Select
                          value={selectedEl.props.fontWeight}
                          onValueChange={(val) => updateElementProps(selectedEl.id, { fontWeight: val })}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Regular</SelectItem>
                            <SelectItem value="600">Semi-Bold</SelectItem>
                            <SelectItem value="bold">Bold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Text Align</label>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={selectedEl.props.textAlign === 'left' ? 'default' : 'outline'}
                            onClick={() => updateElementProps(selectedEl.id, { textAlign: 'left' })}
                            className={selectedEl.props.textAlign === 'left' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                          >
                            <AlignLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={selectedEl.props.textAlign === 'center' ? 'default' : 'outline'}
                            onClick={() => updateElementProps(selectedEl.id, { textAlign: 'center' })}
                            className={selectedEl.props.textAlign === 'center' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                          >
                            <AlignCenter className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={selectedEl.props.textAlign === 'right' ? 'default' : 'outline'}
                            onClick={() => updateElementProps(selectedEl.id, { textAlign: 'right' })}
                            className={selectedEl.props.textAlign === 'right' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                          >
                            <AlignRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {selectedEl.type === 'image' && (
                    <>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Shape</label>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={!selectedEl.props.logoShape || selectedEl.props.logoShape === 'original' ? 'default' : 'outline'}
                            onClick={() => updateElementProps(selectedEl.id, { logoShape: 'original', borderRadius: 0 })}
                            className="flex-1"
                          >
                            <Square className="w-4 h-4 mr-1" />
                            Square
                          </Button>
                          <Button
                            size="sm"
                            variant={selectedEl.props.logoShape === 'circle' ? 'default' : 'outline'}
                            onClick={() => updateElementProps(selectedEl.id, { logoShape: 'circle', borderRadius: selectedEl.w / 2 })}
                            className="flex-1"
                          >
                            <Circle className="w-4 h-4 mr-1" />
                            Circle
                          </Button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">
                          Opacity: {Math.round((selectedEl.props.opacity || 1) * 100)}%
                        </label>
                        <Input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={selectedEl.props.opacity || 1}
                          onChange={(e) => updateElementProps(selectedEl.id, { opacity: Number(e.target.value) })}
                          className="w-full"
                        />
                      </div>
                    </>
                  )}
                  
                  {(selectedEl.type === 'rectangle' || selectedEl.type === 'circle') && (
                    <>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">Fill Color</label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={selectedEl.props.color}
                            onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                            className="h-10 w-16 cursor-pointer"
                          />
                          <Input
                            type="text"
                            value={selectedEl.props.color}
                            onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                            className="flex-1 bg-white font-mono text-xs"
                          />
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <label className="text-xs font-medium text-gray-700 block mb-2">
                          Opacity: {Math.round((selectedEl.props.opacity || 1) * 100)}%
                        </label>
                        <Input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={selectedEl.props.opacity || 1}
                          onChange={(e) => updateElementProps(selectedEl.id, { opacity: Number(e.target.value) })}
                          className="w-full"
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="pt-4 border-t border-gray-200 space-y-2">
                    <Button
                      onClick={() => duplicateElement(selectedEl.id)}
                      variant="outline"
                      size="sm"
                      className="w-full hover:bg-purple-50"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </Button>
                    <Button
                      onClick={() => deleteElement(selectedEl.id)}
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Element
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Select an element to edit</p>
                  <p className="text-xs mt-1">Click any element on the canvas</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
      
      {/* Stock Images Modal */}
      {showStockImages && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl max-w-6xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-white">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Stock Images</h3>
                <p className="text-sm text-gray-600">Powered by Pexels</p>
              </div>
              <Button 
                onClick={() => setShowStockImages(false)} 
                variant="ghost" 
                size="sm"
                className="hover:bg-gray-100 rounded-full w-10 h-10 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              <div className="flex gap-3 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search for images... (e.g., 'business', 'nature', 'office')"
                    className="pl-10 h-12 text-base bg-gray-50 border-gray-200 focus:bg-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchStockImages(e.target.value);
                      }
                    }}
                  />
                </div>
                <Button 
                  onClick={() => {
                    const input = document.querySelector('input[placeholder*="Search for images"]');
                    searchStockImages(input.value);
                  }}
                  className="h-12 px-6 bg-purple-600 hover:bg-purple-700"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>
              
              {loadingStock ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mb-4"></div>
                  <p className="text-gray-600">Finding perfect images...</p>
                </div>
              ) : stockImages.length > 0 ? (
                <div className="overflow-y-auto max-h-[500px] pr-2">
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {stockImages.map(img => (
                      <div
                        key={img.id}
                        onClick={() => addStockImageToCanvas(img.url)}
                        className="group cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all duration-200 hover:shadow-lg hover:scale-105"
                      >
                        <div className="relative">
                          <img
                            src={img.thumbnail}
                            alt=""
                            className="w-full h-40 object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                            <Plus className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                        </div>
                        {img.photographer && (
                          <div className="p-2 bg-gray-50 text-xs text-gray-600 truncate">
                            by {img.photographer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 text-gray-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium text-gray-600 mb-2">Search for stock images</p>
                  <p className="text-sm">Enter a keyword and press Enter or click Search</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Templates and My Designs Modals - Same as Welcome Screen */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Pre-made Templates</h3>
                <p className="text-sm text-gray-600">Choose a template to customize</p>
              </div>
              <Button onClick={() => setShowTemplates(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
              <div className="grid grid-cols-3 gap-4">
                {TEMPLATE_CATEGORIES.map(template => (
                  <div 
                    key={template.id}
                    onClick={() => loadTemplate(template)}
                    className="border-2 rounded-lg overflow-hidden hover:border-purple-500 transition-all cursor-pointer group"
                  >
                    <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-6">
                      <div className="text-center">
                        <Target className="w-16 h-16 mx-auto mb-3 text-purple-600" />
                        <p className="text-lg font-bold text-purple-900">{template.name}</p>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 group-hover:bg-purple-50 transition-colors">
                      <p className="text-sm font-medium text-gray-700 text-center">Click to use</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showMyDesigns && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">My Designs</h3>
                <p className="text-sm text-gray-600">Load and edit your saved designs</p>
              </div>
              <Button onClick={() => setShowMyDesigns(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
              {myDesigns.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Layers className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium text-gray-600 mb-2">No saved designs yet</p>
                  <p className="text-sm">Create a poster and it will be auto-saved here</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {myDesigns.map(design => (
                    <div key={design.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="aspect-square bg-gray-100 flex items-center justify-center p-4">
                        <div className="text-center">
                          <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="text-xs text-gray-600">{design.canvasSize}</p>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {new Date(design.createdAt).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => loadDesign(design)}
                            size="sm"
                            className="flex-1 bg-purple-600 hover:bg-purple-700"
                          >
                            Load
                          </Button>
                          <Button
                            onClick={() => deleteDesign(design.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Image Options Context Menu */}
      {showImageOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setShowImageOptions(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-4 w-72" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Replace Image</h3>
            <div className="space-y-2">
              <Button
                onClick={() => {
                  searchStockImages();
                }}
                variant="outline"
                size="sm"
                className="w-full justify-start hover:bg-purple-50"
              >
                <Search className="w-4 h-4 mr-2" />
                Replace from Stock Images
              </Button>
              <Button
                onClick={() => replaceImageFromUpload(showImageOptions)}
                variant="outline"
                size="sm"
                className="w-full justify-start hover:bg-purple-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Replace from Upload
              </Button>
              <Button
                onClick={() => setShowImageOptions(null)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Background Options Modal */}
      {showBackgroundOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setShowBackgroundOptions(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-4 w-72" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Change Background</h3>
            <div className="space-y-2">
              <Button
                onClick={replaceBackgroundFromStock}
                variant="outline"
                size="sm"
                className="w-full justify-start hover:bg-purple-50"
              >
                <Search className="w-4 h-4 mr-2" />
                Choose from Stock Images
              </Button>
              <Button
                onClick={replaceBackgroundFromUpload}
                variant="outline"
                size="sm"
                className="w-full justify-start hover:bg-purple-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload from Gallery
              </Button>
              <Button
                onClick={setBackgroundAsColor}
                variant="outline"
                size="sm"
                className="w-full justify-start hover:bg-purple-50"
              >
                <Circle className="w-4 h-4 mr-2" />
                Use Solid Color
              </Button>
              <Button
                onClick={() => setShowBackgroundOptions(false)}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Business Templates Selection Modal */}
      {showBusinessTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Choose Business Info Template</h3>
                <p className="text-sm text-gray-600">Select a decorative frame for your business information</p>
              </div>
              <Button onClick={() => setShowBusinessTemplates(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
              <div className="grid grid-cols-3 gap-4">
                {BUSINESS_INFO_TEMPLATES.map(template => (
                  <div 
                    key={template.id}
                    onClick={() => {
                      setSelectedBusinessTemplate(template.id);
                      setShowBusinessTemplates(false);
                      setShowBusinessInfoModal(true);
                    }}
                    className="border-2 rounded-lg overflow-hidden hover:border-purple-500 transition-all cursor-pointer group"
                  >
                    <div className="aspect-video bg-gray-100 flex items-center justify-center p-2">
                      <img 
                        src={template.thumbnail} 
                        alt={template.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="p-3 bg-gray-50 group-hover:bg-purple-50 transition-colors">
                      <p className="text-sm font-medium text-gray-700 text-center">{template.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Business Info Modal */}
      {showBusinessInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Add Business Info</h3>
                <p className="text-sm text-gray-600">
                  {selectedBusinessTemplate 
                    ? `Using ${BUSINESS_INFO_TEMPLATES.find(t => t.id === selectedBusinessTemplate)?.name}` 
                    : 'Fill in your business details'}
                </p>
              </div>
              <Button onClick={() => {
                setShowBusinessInfoModal(false);
                setSelectedBusinessTemplate(null);
              }} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Business Name *</label>
                <Input
                  type="text"
                  placeholder="Your Business Name"
                  value={businessInfoForm.businessName}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, businessName: e.target.value})}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Owner Name</label>
                <Input
                  type="text"
                  placeholder="Owner/Contact Person"
                  value={businessInfoForm.ownerName}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, ownerName: e.target.value})}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Location</label>
                <Input
                  type="text"
                  placeholder="City, State"
                  value={businessInfoForm.location}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, location: e.target.value})}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="contact@business.com"
                  value={businessInfoForm.email}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, email: e.target.value})}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Phone</label>
                <Input
                  type="tel"
                  placeholder="+1 234 567 8900"
                  value={businessInfoForm.phone}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, phone: e.target.value})}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Logo URL (optional)</label>
                <Input
                  type="text"
                  placeholder="https://example.com/logo.png"
                  value={businessInfoForm.logoUrl}
                  onChange={(e) => setBusinessInfoForm({...businessInfoForm, logoUrl: e.target.value})}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Or leave empty and upload logo separately</p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <Button
                onClick={() => {
                  setShowBusinessInfoModal(false);
                  setSelectedBusinessTemplate(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={addBusinessInfoToCanvas}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Add to Canvas
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
